import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Business, Lock, Phone, Visibility, VisibilityOff } from '@mui/icons-material';
import { useFormik } from 'formik';
import { useCookies } from 'react-cookie';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { PARTNER_DASHBOARD } from '../../../services/constants/URIConstants';
import { BackdropComponent } from '../../../shared-components/backdrop/Backdrop';
import { ReleasepilotAffiliation } from '../../../shared-components/branding/ReleasepilotAffiliation';
import { partnerLogin } from '../../../services/request/PartnerAuth';
import { normalizePartnerPhone } from '../../../utils/partnerPortalFormat';

const PartnerLogin: React.FC = () => {
  const navigate = useNavigate();
  const [, setCookie] = useCookies(['auth_token', 'user_role', 'partner_id']);
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: { phone: '', password: '' },
    validationSchema: Yup.object({
      phone: Yup.string()
        .required('Mobile number is required')
        .test('valid-phone', 'Enter a valid 10-digit mobile number', (v) =>
          normalizePartnerPhone(v || '').length === 10
        ),
      password: Yup.string().required('Password is required'),
    }),
    onSubmit: async (values) => {
      setOpen(true);
      try {
        const loginData = await partnerLogin(values.phone, values.password);
        setCookie('auth_token', loginData.token, { path: '/' });
        setCookie('user_role', 'PARTNER', { path: '/' });
        setCookie('partner_id', loginData.partnerId, { path: '/' });
        toast.success(`Welcome, ${loginData.partnerName}!`, { position: 'bottom-center' });
        navigate(PARTNER_DASHBOARD);
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || 'Login failed';
        toast.error(msg, { position: 'bottom-center' });
      } finally {
        setOpen(false);
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0d47a1 0%, #00695c 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        pb: 8,
        position: 'relative',
      }}
    >
      <BackdropComponent open={open} />
      <ToastContainer />
      <Container maxWidth="sm">
        <Paper elevation={24} sx={{ p: 4, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.97)' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Business sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" fontWeight="bold" color="primary">
              Partner Portal
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Sign in with your registered mobile number and password
            </Typography>
          </Box>

          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              label="Mobile number"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={
                (formik.touched.phone && formik.errors.phone) || '10-digit Indian mobile (e.g. 9876543210)'
              }
              sx={{ mb: 2 }}
              placeholder="9876543210"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                      +91
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={
                (formik.touched.password && formik.errors.password) ||
                'Use the password your fund manager shared with you'
              }
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" fullWidth variant="contained" size="large" disabled={open} sx={{ py: 1.5 }}>
              {open ? <CircularProgress size={24} color="inherit" /> : 'Sign in'}
            </Button>
          </form>
        </Paper>
      </Container>
      <Box sx={{ position: 'absolute', bottom: 16, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
        <ReleasepilotAffiliation variant="onDark" />
      </Box>
    </Box>
  );
};

export default PartnerLogin;
